import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild
}                                                          from '@angular/core';
import {Observable, of, Subscription}                      from 'rxjs';
import {ApElementOxydTypeEnum, ApGridPagerType, IGridData} from '../../ap-interface';
import {ApPermissions}                                     from 'invoker-transport';
import {ApDynComponentComponent}                           from '../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                          from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                          from '../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynGridStatusColumnConfig
}                                                          from '../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPropertyColumnConfig,
  FilterType
}                                                          from '../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridEditColumnConfig
}                                                          from '../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {ApDynGridPagerConfig, IDynGridWizardChoiceData}    from '../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {
  ApDynGridGroupColumnConfig
}                                                          from '../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {GetElementOxidCalculatePipe, GetRoundNumericPipe}  from '../../ap-utils';
import {CropTypeStore}                                     from '../../stores/base-data/crop.types.store';
import {filter, map}                                       from 'rxjs/operators';
import {SettingsStore}                                     from '../../stores/base-data/settings.store';
import {MapViewStore}                                      from '../../stores/layout/mapview.store';
import {TranslationStore}                                  from '../../stores/translation/translation.store';
import {GetPermissionPipe}                                 from '../../ap-permission/pipes/get-permission.pipe';
import {GetRoundNumericService}                            from '../../ap-utils/service/get-round-numeric.service';
import {APP_CONFIGURATION}                                 from '../../ap-core/config';
import {GetElementService}                                 from '../../ap-utils/service/ap-get-element.service';
import {ApUtilService}                                     from '../../ap-utils/service/ap-util.service';
import {ObjectFactory}                                     from 'ts-tooling';
import {ApElementType}                                     from '../../ap-interface/enums/ap-elements-type.enum';
import ISettings = Data.BaseData.ISettings;
import ICropTypes = Data.BaseData.ICropTypes;

enum CroptypesOverviewActions {
  Active = 0,
  Inactive = 1,
  ExpectedYield = 2
}

interface ICropTypeGridData extends IGridData<ICropTypes> {
  Status: number;
  StatusClass: string;
  StatusDesc: string;
  KValue: number;
  PValue: number;
  MGValue: number;
  WdN: number;
  SecKValue: number;
  SecPValue: number;
  SecMGValue: number;
  SecWdN: number;
  EuFKValue: number;
  EuFPValue: number;
  EuFMGValue: number;
}

/**
 * Component for the croptypes
 */
@Component({
  selector: 'ap-croptypes-overview',
  template: `
    <ap-dyncomponent [caption]="'Global__Crops'"
                     [canSearch]="true"
                     [headerIcon]="'ap-basedata-icon'"
                     [filterTooltip]="'filterTooltip'"
                     [columns]="columns"
                     [items]="items$ | async"
                     [loading$]="croptypeLoading$"
                     [pager]="pager"
                     [groupable]="true"
                     [selectable]="{checkboxOnly: false, mode: 'multiple'}"
    ></ap-dyncomponent>`,
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class ApCroptypesOverviewComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  public croptypeLoading$ = of(true);
  public items$: Observable<ICropTypeGridData[]> = of([]);
  public columns: ApDynGridColumnConfigBase[] = [];
  public pager: ApDynGridPagerConfigBase;

  private _applyClicked = new EventEmitter<any>();
  private _settings: ISettings;
  private _subscriptions: Array<Subscription> = [];

  constructor(private cropTypeStore: CropTypeStore,
              private translationStore: TranslationStore,
              private mapViewStore: MapViewStore,
              private settingsStore: SettingsStore,
              private permissionPipe: GetPermissionPipe,
              private roundNumericService: GetRoundNumericService,
              private roundNumericPipe: GetRoundNumericPipe,
              private elementOxideCalculate: GetElementOxidCalculatePipe,
              private elementService: GetElementService) {

  }

  ngOnInit(): void {
    this.mapViewStore.hideMapView();
    this.croptypeLoading$ = this.cropTypeStore.Listen(s => s.loading);
    this._settings = this.settingsStore.FirstSetting;
    this.items$ = this._getItems();
    this.columns = this._getColumns();
    this.pager = this._getPager(false);
  }

  ngAfterContentInit(): void {
    this._subscriptions.push(this._applyClicked.subscribe((e) => this._onApplyClicked(e)));
    this._subscriptions.push(this.settingsStore.FirstSetting$.subscribe(s => this._settings = s));
    this._subscriptions.push(this.permissionPipe.transform(ApPermissions.EDIT_CROPTYPES).subscribe(permission => {
      this.pager = this._getPager(permission);
    }));
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(d => {
      d.unsubscribe();
    });
  }

  private _getColumns(): ApDynGridColumnConfigBase[] {
    const columns: ApDynGridColumnConfigBase[] = [
      new ApDynGridStatusColumnConfig({
        field: 'Status',
        showHeaderTitle: true,
        class: 'StatusClass',
        description: 'StatusDesc',
        filterable: true,
        groupIndex: 0,
        groupDesc: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.Shortname',
        title: 'Global__Acronym',
        filterable: true,
        width: 70,
        headerFilterable: true,
        sortIndex: 0,
        sortDesc: false,
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.Description',
        title: 'Global__Description',
        filterable: true,
        width: 120,
        headerFilterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.ExpectedYield',
        title: 'Global__Average_Yield_Unit',
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        width: 60,
        headerFilterable: true,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [this.settingsStore.FirstSetting.DigitsAfterDecimalPoint]
        }]
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__Detraction_MainCrop_Unit',
        headerStyle: {'text-align': 'center'},
        style: {border: '1px solid #2c3237'},
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: 'PValue',
            title: this.elementService.GetElementStringByString(ApElementType.P),
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            width: 40,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'KValue',
            title: this.elementService.GetElementStringByString(ApElementType.K),
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            width: 40,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'MGValue',
            title: this.elementService.GetElementStringByString(ApElementType.Mg),
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            width: 40,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'WdN',
            title: 'Global__Agent_N',
            width: 40,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            hide: true,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction]
            }]
          })
        ]
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.MsRatio',
        title: 'Global__Main_Secondary_Crop_Ratio',
        hide: true,
        width: 50,
        cssClass: 'right',
        filterType: FilterType.NUMBER,
        filterable: true,
        headerFilterable: true,
        pipes: [{
          pipe: this.roundNumericPipe,
          args: [APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction]
        }]
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__Detraction_SecondCrop_Unit',
        headerStyle: {'text-align': 'center'},
        style: {border: '1px solid #2c3237'},
        hide: true,
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: 'SecPValue',
            title: this.elementService.GetElementStringByString(ApElementType.P),
            width: 40,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            hide: true,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'SecKValue',
            title: this.elementService.GetElementStringByString(ApElementType.K),
            width: 40,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            hide: true,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'SecMGValue',
            title: this.elementService.GetElementStringByString(ApElementType.Mg),
            width: 40,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            hide: true,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'SecWdN',
            title: 'Global__Agent_N',
            width: 40,
            cssClass: 'right',
            filterType: FilterType.NUMBER,
            hide: true,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction]
            }]
          })
        ]
      })
    ];
    if (this._settings.UseMethodEuf) {
      columns.push(
        new ApDynGridGroupColumnConfig({
          title: 'Global__Detraction_EUF_MainCrop_Unit',
          headerStyle: {'text-align': 'center'},
          style: {border: '1px solid #2c3237'},
          hide: true,
          groupColumns: [
            new ApDynGridPropertyColumnConfig({
              field: 'EuFPValue',
              title: this.elementService.GetElementStringByString(ApElementType.P),
              width: 40,
              hide: true
            }),
            new ApDynGridPropertyColumnConfig({
              field: 'EuFKValue',
              title: this.elementService.GetElementStringByString(ApElementType.K),
              width: 40,
              hide: true
            }),
            new ApDynGridPropertyColumnConfig({
              field: 'EuFMGValue',
              title: this.elementService.GetElementStringByString(ApElementType.Mg),
              width: 40,
              hide: true
            })
          ]
        }),
      );
    }
    columns.push(
      new ApDynGridEditColumnConfig({
        data: [{
          callback: ApUtilService.createEventEmitter(this._onSelectedToggle.bind(this), this._subscriptions),
          text: (dataItem) => dataItem.SourceItem.Selected ? 'Global__Disable' : 'Global__Enable'
        }]
      })
    );
    return columns;
  }

  private _getPager(isHasPermission: boolean): ApDynGridPagerConfig {
    return new ApDynGridPagerConfig({
      listMassEditing: [
        {
          id: CroptypesOverviewActions.Active,
          name: this.translationStore.FindTranslationForSelectedLanguage('Global__Enable'),
          type: ApGridPagerType.None,
          values: null
        } as IDynGridWizardChoiceData,
        {
          id: CroptypesOverviewActions.Inactive,
          name: this.translationStore.FindTranslationForSelectedLanguage('Global__Disable'),
          type: ApGridPagerType.None,
          values: null
        } as IDynGridWizardChoiceData,
        {
          id: CroptypesOverviewActions.ExpectedYield,
          name: this.translationStore.FindTranslationForSelectedLanguage('Global__Average_Yield_Unit'),
          type: ApGridPagerType.Numeric,
          values: null
        } as IDynGridWizardChoiceData
      ],
      apply: this._applyClicked,
      hasPermission: isHasPermission,
    });
  }

  private _getItems(): Observable<ICropTypeGridData[]> {
    return this.cropTypeStore.Listen(s => s.data)
      .pipe(
        filter(d => !!d),
        map(croptypes => croptypes.Convert<ICropTypeGridData>((ct: any) => {
            const kValue = this._elementOxidCalculate(ct.MainwdK, ApElementOxydTypeEnum.K);
            const pValue = this._elementOxidCalculate(ct.MainwdP, ApElementOxydTypeEnum.P);
            const mgValue = this._elementOxidCalculate(ct.MainwdMg, ApElementOxydTypeEnum.Mg);
            const secKValue = this._elementOxidCalculate(ct.SecwdK, ApElementOxydTypeEnum.K);
            const secPValue = this._elementOxidCalculate(ct.SecwdP, ApElementOxydTypeEnum.P);
            const secMGValue = this._elementOxidCalculate(ct.SecwdMg, ApElementOxydTypeEnum.Mg);
            const euFKValue = this._elementOxidCalculate(ct.EuFiltrationK, ApElementOxydTypeEnum.K);
            const euFPValue = this._elementOxidCalculate(ct.EuFiltrationP, ApElementOxydTypeEnum.P);
            const euFMGValue = this._elementOxidCalculate(ct.EuFiltrationMg, ApElementOxydTypeEnum.Mg);
            return {
              Id: ct.Id,
              SourceItem: ct,
              Status: ct.Selected ? 1 : -1,
              StatusClass: ct.Selected ? 'ap-status-green' : 'ap-status-red',
              StatusDesc: ct.Selected ? 'Global__Active' : 'Global__Inactive',
              KValue: this.roundNumericService.roundAsNumber(kValue, APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction),
              PValue: this.roundNumericService.roundAsNumber(pValue, APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction),
              MGValue: this.roundNumericService.roundAsNumber(mgValue, APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction),
              WdN: this.roundNumericService.roundAsNumber(ct.MainwdN, APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction),
              SecKValue: this.roundNumericService.roundAsNumber(secKValue, APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction),
              SecPValue: this.roundNumericService.roundAsNumber(secPValue, APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction),
              SecMGValue: this.roundNumericService.roundAsNumber(secMGValue, APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction),
              SecWdN: this.roundNumericService.roundAsNumber(ct.SecwdN, APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction),
              EuFKValue: this.roundNumericService.roundAsNumber(euFKValue, APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction),
              EuFPValue: this.roundNumericService.roundAsNumber(euFPValue, APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction),
              EuFMGValue: this.roundNumericService.roundAsNumber(euFMGValue, APP_CONFIGURATION.SpecialDecimalPlaces.CropExtraction)
            };
          })
        )
      );
  }

  private _onApplyClicked(data: any): void {
    const modified: ICropTypes[] = [];
    let yieldChanged = false;
    for (const anyRow of this.dynComponent.getSelectedItems()) {
      let row = ObjectFactory.Copy(anyRow['SourceItem'] as ICropTypes);
      if (!row) {
        continue;
      }
      // copy data here we won't change the original value in the grid!
      row = ObjectFactory.Copy(row);
      switch (data.object.id) {
        case CroptypesOverviewActions.Active:
          row.Selected = true;
          break;
        case CroptypesOverviewActions.Inactive:
          row.Selected = false;
          break;
        case CroptypesOverviewActions.ExpectedYield:
          row.ExpectedYield = data.value;
          yieldChanged = true;
          break;
      }
      modified.Add(row);
    }
    this.cropTypeStore.saveCroptypes(modified, yieldChanged);
  }

  private _elementOxidCalculate(value: number, unit: ApElementOxydTypeEnum): number {
    return this.elementOxideCalculate.transform(
      value, unit, this._settings.UnitElementOxyd
    );
  }

  private _onSelectedToggle(item: any): void {
    const cropType = ObjectFactory.Copy(this.cropTypeStore.CropTypes.Find((crop) => !!crop && !!item && crop?.Id === item?.Id));
    cropType.Selected = !cropType.Selected;
    this.cropTypeStore.saveCroptypes([cropType], false);
  }
}
